import { default as uniqid } from 'uniqid'; // eslint-disable-line import/no-named-default
import { getPromise, addPromise, removePromise } from './utils/authPromiseCache';
let authStrategies = [];
let cacheStrategy;
function getCacheStrategy() {
    if (!cacheStrategy) {
        throw new Error(`No token cache strategy configured`);
    }
    return cacheStrategy;
}
/**
 * Register an authentication strategy
 */
export function addAuthStrategy(strategy) {
    authStrategies.push(strategy);
}
/**
 * Remove all registered strategies
 *
 * Note this is included for testing purposes only and shouldn't be required at runtime
 */
export function clearStrategies() {
    authStrategies = [];
    cacheStrategy = undefined;
}
/**
 * Register a token caching strategy
 */
export function setCacheStrategy(strategy) {
    cacheStrategy = strategy;
}
/**
 * Acquire a token for the given auth config
 */
export async function authenticate(config, forceRefresh) {
    // Look for a matching token in cache and return it if it's still valid
    const existingToken = cacheStrategy && (await cacheStrategy.getToken(config));
    if (existingToken && !forceRefresh) {
        return existingToken;
    }
    const authStrategy = authStrategies.find((strategy) => strategy.supports(config));
    if (authStrategy) {
        // Look for matching promise in cache
        const cachedPromise = getPromise(authStrategy, config);
        if (cachedPromise) {
            return cachedPromise;
        }
        // eslint-disable-next-line no-async-promise-executor
        const authPromise = new Promise(async (resolve, reject) => {
            try {
                const token = await authStrategy.authenticate(config);
                if (token) {
                    token.id = token.id || uniqid();
                }
                if (cacheStrategy) {
                    await cacheStrategy.addToken(token);
                }
                resolve(token);
            }
            catch (e) {
                reject(e);
            }
            finally {
                removePromise(authStrategy, config);
            }
        });
        // Add auth promise to cache
        addPromise({
            promise: authPromise,
            config
        });
        return authPromise;
    }
    throw new Error(`No authentication strategy supports the supplied auth config`);
}
/**
 * Checks if a token matching the supplied config exists in cache but doesn't return it.
 * Can be used to check if a user has already logged in
 */
export async function hasToken(config) {
    return !!(cacheStrategy && (await cacheStrategy.getToken(config))) || false;
}
/**
 * Add an existing token to the cache (eg. one you retrieved without using this library)
 */
export async function cacheToken(token) {
    return getCacheStrategy().addToken(token);
}
/**
 * Remove a token from the cache
 */
export async function removeToken(token) {
    return getCacheStrategy().removeToken(token);
}
//# sourceMappingURL=core.js.map